<template>
  <!-- My Saved Searches drawer list (saved search picker) -->

  <v-card
    width="100%"
    class="favourites"
    :class="{ 'favourites--dark': dark }"
    flat
  >
    <header class="favourites__header">
      <v-list class="py-0">
        <v-list-item class="favourites__title px-2 ma-0">
          <v-list-item-icon class="favourites__title-icon-wrap mr-2 my-0">
            <v-icon class="favourites__title-icon" medium>mdi-view-headline</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="font-weight-bold subtitle-1 favourites__title-text">
              <span>My Saved Searches</span><sup class="favourites__help-sup">
                <a
                  href="https://docs.tendertrace.com/guide/user-documentation/saving-your-work/search-profiles"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Saved searches documentation"
                >
                  <v-icon x-small class="favourites__help-icon">mdi-help-circle-outline</v-icon>
                </a>
              </sup>
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="ma-0">
            <v-btn icon @click="loading = true, getSavedSearches()">
              <v-icon class="favourites__action-icon">mdi-refresh</v-icon>
            </v-btn>
          </v-list-item-action>
          <v-list-item-action class="ma-0">
            <v-btn icon aria-label="Close saved searches" @click="$emit('closeDrawer')">
              <v-icon class="favourites__close-icon">mdi-close</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <v-divider class="favourites__divider"></v-divider>
      <v-card flat class="favourites__search-wrap px-2 py-2 mx-2 my-2">
        <v-text-field
          class="favourites__search-field"
          :class="{ 'favourites__search-field--dark': dark }"
          append-icon="mdi-magnify"
          rounded
          v-model="search"
          dense
          :solo="dark"
          :solo-inverted="!dark"
          hide-details
          flat
          label="Search saved searches..."
          color="rgb(255, 20, 99)"
        ></v-text-field>
      </v-card>
    </header>

    <div class="favourites__body">
      <div v-if="loading" class="favourites__loading text-center py-8">
        <v-progress-circular indeterminate color="#ff1463" />
      </div>
      <v-list v-else>
      <v-list-item-group
        v-if="filteredSearches.length > 0"
        v-model="selectedItem"
        color="rgb(255, 20, 99)"
      >
        <v-list-item
          v-for="item in filteredSearches"
          :key="item.id"
          class="favourites__search-row"
          @click="retrieveSearch(item.search_query, item.id)"
        >
          <v-tooltip left dark color="#000000" style="opacity: 0.7;">
            <template v-slot:activator="{ on, attrs }">
              <v-list-item-content
                v-bind="attrs"
                v-on="on"
                class="favourites__search-item-content py-2"
              >
                <v-list-item-title class="wrap-text favourites__search-item">
                  <span v-if="item.name && item.name != 'unnamed search'">
                    {{ item.name }}
                  </span>
                  <span v-else>
                    {{ item.plain_text_query }}
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </template>
            <span class="tooltip">{{ item.plain_text_query }}</span>
          </v-tooltip>
        </v-list-item>
      </v-list-item-group>
      <v-list-item
        v-else-if="saved_searches.length > 0"
        class="favourites__empty-filter text-center py-4"
      >
        <v-list-item-content>
          <v-list-item-title class="favourites__empty-filter-text">
            No saved searches match your search.
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-list-item
        v-if="saved_searches.length === 0"
        class="ma-0 pa-0 favourites__explainer-outer"
      >
        <v-card flat class="ma-0 pa-0 favourites__explainer-outer-card">
          <v-card-text class="favourites__explainer-wrap pl-3 pt-0">
            <v-list-group
              v-model="explainerOpen"
              class="favourites__explainer"
              no-action
              sub-group
            >
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title
                    class="subtitle-2 text-decoration-underline font-weight-bold favourites__explainer-heading"
                    color="primary"
                  >
                    Saved Search Explained
                  </v-list-item-title>
                </v-list-item-content>
              </template>
              <v-list-item class="pa-0 favourites__explainer-item">
                <v-card flat tile class="favourites__explainer-content pl-1">
                  <p class="favourites__explainer-text">
                    You can save any searches that you run. This means you can
                    quickly rerun those searches when you log back in.
                    Tendertrace will automatically monitor those searches and
                    notify you of any new matches. New matches appear in your
                    daily digest email and on the Dashboard under New Matches.
                  </p>
                  <p class="favourites__explainer-text">
                    Saved searches also save the search tab you are on. So for
                    example, if you were on the Leaderboard tab when you saved
                    the search profile, clicking on the saved profile will run
                    the search and also take you to the tab you were on when you
                    saved the profile initially.
                  </p>
                  <p class="favourites__explainer-text">
                    To start saving searches:
                    <ul>
                      <li>
                        Run a search for contracts or tenders of interest.
                      </li>
                      <li>
                        In the filter description section of the screen, click
                        the button labeled "Save Search" to have Tendetrace
                        start monitoring that search on your behalf.
                      </li>
                    </ul>
                  </p>
                  <p class="favourites__explainer-link">
                    <router-link
                      class="favourites__explainer-link-text"
                      :to="{
                        name: 'search',
                        query: {
                          disposition: ['Contract Notice', 'Grant'],
                          expiry_from: new Date().getFullYear(),
                          expiry_to: new Date().getFullYear(),
                          sort_order: 'asc'
                        }
                      }"
                      >Click here to start exploring.</router-link
                    >
                  </p>
                </v-card>
              </v-list-item>
            </v-list-group>
          </v-card-text>
        </v-card>
      </v-list-item>
    </v-list>
    </div>
  </v-card>
</template>

<script>
import { bus } from "../main.js";
import axios from "axios";
import { getInstance } from "../auth/index.js";

export default {
  components: {},

  props: {
    dark: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: true,
      menu: false,
      search: "",
      explainerOpen: false,
      saved_searches: []
    };
  },

  computed: {
    filteredSearches() {
      if (this.saved_searches.length > 0){
        return this.saved_searches.filter(search =>
          (search.name && search.name !== "unnamed search"
            ? search.name
            : search.plain_text_query
          )
            .toLowerCase()
            .includes(this.search.toLowerCase())
        );
      }
      else{
        return this.saved_searches;
      }
      
    }
  },

  mounted() {
    this.getSavedSearches();

    bus.$on("refresh_lists", refresh_lists => {
      this.getSavedSearches();
    });

    bus.$on("search-complete", search_complete => {
      this.getSavedSearches();
    });
  },

  methods: {
    retrieveSearch(query, search_id) {
      var qry = query;
      qry["search_id"] = search_id;

      this.$router.push({ path: "/search", query: qry });
      bus.$emit(bus.$emit("hide-side-navs", true));
    },

    close() {
      this.$emit("close", true);
    },

    cancel() {
      this.menu = false;
      bus.$emit("favourites-close", this.menu);
    },

    async getSavedSearches() {
      const instance = getInstance();
      const accessToken = await instance.getTokenSilently();

      axios
        .get(process.env.VUE_APP_API_SEARCHES, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        })
        .then(response => {
          this.saved_searches = response.data.saved_searches;
          this.explainerOpen = this.saved_searches.length === 0;
          this.loading = false;
          return this.saved_searches;
        })
        .catch(error => {
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    }
  }
};
</script>

<style scoped>
.favourites {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.favourites__header {
  flex-shrink: 0;
}

.favourites__title.v-list-item {
  min-height: 48px;
}

.favourites__title-icon-wrap {
  align-self: center;
  margin-right: 8px !important;
  min-width: auto;
}

.favourites__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.favourites__title-icon,
.favourites__title-text {
  color: #12253a !important;
}

.favourites__help-sup {
  line-height: 0;
  margin-left: 2px;
}

.favourites__help-sup a {
  text-decoration: none;
  opacity: 0.85;
}

.favourites__help-sup a:hover {
  opacity: 1;
}

.favourites__help-icon {
  color: #12253a !important;
}

.favourites__action-icon {
  color: #12253a !important;
}

.favourites__close-icon {
  color: rgba(0, 0, 0, 0.54) !important;
}

.favourites__search-item {
  color: #12253a !important;
}

.favourites__explainer-heading,
.favourites__explainer-text {
  color: #12253a;
}

.favourites__explainer-link a {
  color: #12253a;
}

.favourites__empty-filter-text {
  color: rgba(0, 0, 0, 0.54);
  font-size: 0.875rem;
}

.favourites--dark {
  background: transparent !important;
}

.favourites--dark .favourites__title-icon,
.favourites--dark .favourites__title-text {
  color: rgba(255, 255, 255, 0.95) !important;
}

.favourites--dark .favourites__action-icon,
.favourites--dark .favourites__close-icon,
.favourites--dark .favourites__help-icon {
  color: rgba(255, 255, 255, 0.9) !important;
}

.favourites--dark .favourites__divider {
  border-color: rgba(255, 255, 255, 0.12);
}

.favourites--dark .favourites__search-item {
  color: rgba(255, 255, 255, 0.9) !important;
}

.favourites--dark .favourites__explainer-heading,
.favourites--dark .favourites__explainer-text {
  color: rgba(255, 255, 255, 0.85);
}

.favourites--dark .favourites__explainer-link a,
.favourites--dark .favourites__explainer-link-text {
  color: #ff1463 !important;
}

.favourites--dark .favourites__explainer-link a:hover,
.favourites--dark .favourites__explainer-link-text:hover {
  color: #ff4d8a !important;
}

.favourites--dark .favourites__empty-filter-text {
  color: rgba(255, 255, 255, 0.6) !important;
}

.favourites--dark .favourites__search-wrap {
  background: transparent !important;
}

.favourites__search-field--dark >>> .v-input__slot {
  background: #ffffff !important;
  box-shadow: none !important;
}

.favourites__search-field--dark >>> fieldset {
  border: 1px solid #12253a !important;
}

.favourites__search-field--dark >>> .v-input__append-inner .v-icon {
  color: #ff1463 !important;
}

.favourites__search-field--dark >>> input {
  color: #12253a !important;
  caret-color: #12253a;
}

.favourites__search-field--dark >>> .v-label {
  color: rgba(18, 37, 58, 0.6) !important;
}

.favourites__search-field--dark.v-input--is-focused >>> fieldset {
  border-color: #12253a !important;
}

.favourites--dark >>> .v-list-item__title,
.favourites--dark >>> .v-list-item__icon .v-icon {
  color: rgba(255, 255, 255, 0.85) !important;
}

.favourites--dark .favourites__explainer-wrap {
  background: #12253a !important;
}

.favourites--dark .favourites__explainer-content,
.favourites--dark .favourites__explainer-item,
.favourites--dark .favourites__explainer-outer,
.favourites--dark .favourites__explainer-outer-card {
  background: #12253a !important;
}

.favourites--dark >>> .favourites__explainer .v-list-group__header,
.favourites--dark >>> .favourites__explainer .v-list-group__items,
.favourites--dark >>> .favourites__explainer .v-list-group__items .v-list-item,
.favourites--dark >>> .favourites__explainer .v-list-group__items .v-card {
  background: #12253a !important;
}

.favourites__search-row {
  cursor: pointer;
}

.favourites__search-item-content {
  width: 100%;
}

.favourites__search-row:hover .favourites__search-item {
  text-decoration: underline !important;
}

.favourites--dark >>> .favourites__search-row .v-list-item__content {
  color: rgba(255, 255, 255, 0.9) !important;
}

.v-application--is-ltr .v-list-group--sub-group .v-list-group__header {
  padding-left: 0px !important;
}

.wrap-text {
  -webkit-line-clamp: unset !important;
}

.tooltip {
  display: inline-block;
  max-width: 360px;
}

.border {
  border: 1px solid rgb(255, 20, 99);
}
</style>
